import { MouseEvent, useRef, useState } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useSetRecoilState } from "recoil";

import { VideoContainerFeed } from "../styledComponents";
import {
  countClipImpression,
  dateFormatWithDistanceSuffix,
} from "../../../Utilities/Clip";
import { VideoPlayer } from "../../../VideoPlayer";
import { getAvatarImage } from "../../../Utilities/User";
import { IContentItem } from "../@types";
import { Link } from "../../../Components/Link";
import { ContentItemBadge } from "../ContentItemBadge";
import styled from "styled-components";
import { ModalType, TTags } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { ClipContext } from "MVVM/Views/ClipExperience/@types";
import { Avatar } from "MVVM/Components/AvatarV2";
import { ClipFullModalState } from "MVVM/State/modals";

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

interface IContentItemProps {
  isMontage?: boolean;
  isWrapped?: boolean;
  item: IContentItem;
  subtextColor?: string;
  title: string;
  borderColor?: string;
  navigationContext?: ClipContext;
  isProsFeed?: boolean;
}

const ContentItem = ({
  isMontage,
  item,
  subtextColor = "chalk",
  title,
  borderColor,
  navigationContext,
  isProsFeed,
}: IContentItemProps) => {
  const [active, setActive] = useState(false);
  const [missingThumbnailChecked, setMissingThumbnailChecked] = useState(false);
  const setClipFullModalState = useSetRecoilState(ClipFullModalState);
  const thumbnail =
    item.thumbnailStandardUrl ||
    (item.clipImageThumb?.startsWith("http")
      ? item.clipImageThumb
      : `${REACT_APP_S3_CLIP_BUCKET}/${item.clipImageThumb}`);
  const thumbnailOg =
    item.thumbnailOverlayUrl ||
    `${REACT_APP_S3_CLIP_BUCKET}/${item.clipSnapshot}`;
  const userProfileUrl = `/u/${item.user?.username || item.user?._id}`;
  const clipLink = `/clip/${item.shareId}`;

  const { badge } = item;

  const handleClick = (event?: MouseEvent) => {
    analytics.track("Homepage Feed - Click", {
      type: title,
    });

    // Open in new tab if ctrl/cmd click
    if (event?.ctrlKey || event?.metaKey) {
      window.open(clipLink, "_blank");
      return;
    }

    // Open clip in modal
    setClipFullModalState({
      isOpen: ModalType.Clip,
      shareId: item.shareId,
      orientation: "LANDSCAPE",
      contextOverride: navigationContext || {
        type: "default",
        metadata: {
          feedTitle: title,
          source: "modal",
        },
      },
    });
  };

  const getTagValue = (tags: TTags[] | undefined, key: string) => {
    return tags?.find((tag: TTags) => tag.key === key)?.value;
  };

  const playerPhoto = getTagValue(item.tags, "PARTNER_playerPhoto");
  const playerName = getTagValue(item.tags, "PARTNER_playerName");

  return (
    <Flex
      flexDirection="column"
      style={{ height: "auto", width: "100%", position: "relative" }}
    >
      <ResponsiveContainer
        onMouseEnter={() => {
          if (!(item.previewUrl || item.clipPreviewPath)) return;

          setActive(true);
          countClipImpression(item.shareId, isMontage);
        }}
        onMouseLeave={() => setActive(false)}
        style={{
          border: borderColor ? `1px solid ${borderColor}` : "none",
          borderRadius: 9,
          overflow: "hidden",
        }}
      >
        {badge && <ContentItemBadge badge={badge} />}
        <Image
          src={thumbnailOg}
          width="100%"
          style={{ position: "absolute", zIndex: 1, borderRadius: 9 }}
          onClick={(e: MouseEvent) => handleClick(e)}
          onError={(e) => {
            if (missingThumbnailChecked) return true;
            e.currentTarget.src = thumbnail;
            setMissingThumbnailChecked(true);
          }}
        />
        <VideoPreview
          active={active}
          item={item}
          title={title}
          handleClick={handleClick}
        />
      </ResponsiveContainer>
      <Flex mt={4} style={{ gap: 12 }}>
        <Avatar
          src={isProsFeed ? playerPhoto : getAvatarImage(item.user)}
          size="sm"
          profileCardUsername={item.user?.username || item.user?._id}
          userGroup={isProsFeed ? "PRO_PLAYER" : item.user?.userGroup?.type}
          rank={item.user?.rank}
          link={isProsFeed ? null : userProfileUrl}
        />
        <Box>
          <Text
            fontWeight={500}
            style={{ cursor: "pointer" }}
            onClick={(event: MouseEvent) => {
              analytics.track("Homepage Feed - Title Click", {
                type: title,
              });

              // Open in new tab if ctrl/cmd click
              if (event?.ctrlKey || event?.metaKey) {
                window.open(clipLink, "_blank");
                return;
              }

              // Open clip in modal
              setClipFullModalState({
                isOpen: ModalType.Clip,
                shareId: item.shareId,
                orientation: "LANDSCAPE",
                contextOverride: navigationContext || {
                  type: "default",
                  metadata: {
                    feedTitle: title,
                    source: "modal",
                  },
                },
              });
            }}
          >
            {item.title || item?.userClipTitle || item.clipTitle}
          </Text>
          {isProsFeed ? (
            <Text fontWeight={500} fontSize={1} color={subtextColor} mt={3}>
              {playerName}
            </Text>
          ) : (
            <Link
              to={userProfileUrl}
              onClick={() =>
                window.scroll({
                  top: 0,
                  left: 0,
                  behavior: "smooth",
                })
              }
            >
              <Text fontWeight={500} fontSize={1} color={subtextColor} mt={3}>
                {item.user?.username}
              </Text>
            </Link>
          )}
          <Flex
            style={{ gap: 4 }}
            fontSize={1}
            color={subtextColor}
            fontWeight={500}
            mt={2}
          >
            {item.views >= 5 && (
              <>
                <Text>{item.views} views</Text>
                <Text>&bull;</Text>
              </>
            )}
            <Text>
              {dateFormatWithDistanceSuffix(
                item.createdAt || item.createdDate || "",
              )}
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

const VideoPreview = ({
  active,
  handleClick,
  item,
  title,
}: {
  active: boolean;
  handleClick: (event?: MouseEvent) => void;
  item: IContentItem;
  title: string;
}) => {
  const mp4src =
    item.previewUrl || `${REACT_APP_S3_CLIP_BUCKET}/${item.clipPreviewPath}`;
  const poster =
    item.thumbnailStandardUrl ||
    `${REACT_APP_S3_CLIP_BUCKET}/${item.clipImageThumb}`;
  const videoPlayerRef = useRef(null);

  return (
    <VideoContainerFeed
      clickable={true}
      onClick={(e: MouseEvent) => handleClick(e)}
      style={{
        transition: "opacity 0.5s ease-in-out",
        opacity: `${active ? 1 : 0}`,
      }}
    >
      {active && (
        <VideoPlayer
          refForVideoPlayer={videoPlayerRef}
          mp4Source={mp4src}
          hlsSource={item.hlsSrc}
          loop={true}
          playAnalyticsProperties={{
            shareId: item.shareId,
            clipId: item._id,
            clipOwner: item.user,
            total_views: item.views,
            game: item.game,
          }}
          watchAnalyticsProperties={{
            clipId: item._id,
            collection: title,
            collectionType: "Generic",
            item: item.shareId,
            itemType: "Clip",
            action: "preview",
          }}
          options={{
            controls: false,
            aspectRatio: "16:9",
            poster,
            playsinline: true,
            autoplay: "muted",
            userActions: {
              click: false,
            },
          }}
        />
      )}
    </VideoContainerFeed>
  );
};

const ResponsiveContainer = styled(Box)`
  aspect-ratio: 16/9;
  position: relative;
  width: 100%;
  cursor: pointer;
`;

export { ContentItem };
